import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { TopicFocusEntryInterface } from '@whizz/api-model';
import type { Cell } from 'react-table';
import type { CustomColumnProps, CustomRowProps } from '../../../../components/table/types';
import type { CancelTopicAccessors, StudentCheckboxProps } from './types';
import { ChoiceGroup, Table } from '../../../../components';
import SelectStudentsTableLayout from './SelectStudentsTableLayout';
import Progressions from '../Progressions';
import { DateTime } from 'luxon';

type Props = {
  topicFocusEntries: TopicFocusEntryInterface[];
  selectedStudentsIds: string[];
  onSelect(student: string): void;
  onSelectAll(studentsIds: string[]): void
};

const CancelTopicTable = ({ topicFocusEntries, selectedStudentsIds, onSelect, onSelectAll }: Props): JSX.Element => {
  const { translate, locale } = useI18n();

  const columns = [
    {
      accessor: 'select',
      disableSortBy: true,
      Header: () => null,
      Cell: (cell: Cell) => {
        const values: StudentCheckboxProps = cell.value;
        return (
          <ChoiceGroup
            id={`cancel_${values.id}`}
            name={'cancel_students'}
            label={values.label}
            hideLabel
            disabled={values.disabled ?? false}
            checked={values.disabled ? false : selectedStudentsIds.includes(values.id)}
            onChange={() => onSelect(values.id)}
          />
        );
      },
    },
    {
      accessor: 'name',
      Header: translate('components.TABLES.STUDENTS.NAME'),
    },
    {
      accessor: 'topic',
      disableSortBy: true,
      Header: translate('pages.TOPIC.CANCEL.MODAL.current_topic'),
    },
    {
      accessor: 'progressions',
      Header: translate('pages.TOPIC.CANCEL.MODAL.progressions'),
      Cell: (cell: Cell) => {
        return (
          <Progressions value={cell.value} />
        );
      },
    },
    {
      accessor: 'end_date',
      Header: translate('pages.TOPIC.CANCEL.MODAL.end_date'),
    },
  ] as CustomColumnProps<CancelTopicAccessors>[];

  const getFormattedData = (): CustomRowProps<CancelTopicAccessors>[] => (
    topicFocusEntries.reduce((acc: CustomRowProps<CancelTopicAccessors>[], curr) => {
      const topicFocusEntry = curr;

      if (null == topicFocusEntry.topicFocus.current) {
        return acc;
      }

      const currentTopicFocus = topicFocusEntry.topicFocus.current;
      const fullName = `${topicFocusEntry.firstname} ${topicFocusEntry.lastname}`;
      const endDate = DateTime.fromISO(currentTopicFocus.endDate as string);

      return [
        ...acc,
        {
          select: {
            id: topicFocusEntry.studentId.toString(),
            label: fullName,
          } as Omit<StudentCheckboxProps, 'disabled'>,
          name: fullName,
          topic: currentTopicFocus.topic,
          progressions: currentTopicFocus.progress.split('/')[0].trim(),
          end_date: endDate.setLocale(locale?.id ?? 'en-gb').toLocaleString(),
        },
      ];
    }, [])
  );

  const rows = getFormattedData();

  return (
    <SelectStudentsTableLayout
      id={'cancel'}
      selectAllProps={{
        indeterminate: selectedStudentsIds.length > 0 && selectedStudentsIds.length !== topicFocusEntries.length,
        checked: selectedStudentsIds.length > 0,
        onChange: () => onSelectAll(rows.map((row) => (row.select as StudentCheckboxProps).id)),
      }}
    >
      <div className={'overflow-auto'} style={{ maxHeight: 400 }}>
        <Table
          id={'cancel_topic_table'}
          color={'white'}
          cols={columns}
          content={rows}
          initialState={{
            sortBy: [
              {
                id: 'name',
                desc: false,
              },
            ],
          }}
        />
      </div>
    </SelectStudentsTableLayout>
  );
};

CancelTopicTable.displayName = 'CancelTopicTable';

export default CancelTopicTable;
